/* istanbul ignore file - better tested visually */

import { faCube, faCubes, faEye, faEyeSlash, faLightbulb, faSquare } from '@fortawesome/pro-light-svg-icons';
import { faLightbulbOn, faSquareCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Animation } from '@partful/features/instructions/animations/model/animation';
import { classNames } from '@partful/shared/lib/classNames';
import {
  ComponentProps,
  KeyboardEventHandler,
  memo,
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Mesh, Object3D } from 'three';
import styles from './KeyframeEditor.module.scss';

type FlatObject = { object: Object3D; depth: number };

function flatten(object: Object3D, depth: number = 0): FlatObject[] {
  return [{ object, depth }, ...object.children.flatMap((child) => flatten(child, depth + 1))];
}

export type KeyframeEditorProps = {
  model?: Object3D;
  animation?: Animation;
  time: number;
  onChangeTime: (time: number) => void;
  durationSeconds?: number;
  stepSizeSeconds?: number;
  selection?: Object3D[];
  onSelect?: (selection: Object3D[]) => void;
  onToggleVisibility?: (object: Object3D) => void;
  onToggleHighlight?: (object: Object3D) => void;
} & Omit<ComponentProps<'div'>, 'onSelect'>;

export function KeyframeEditor({
  model,
  animation,
  time,
  onChangeTime,
  durationSeconds = 10,
  selection = [],
  onSelect,
  onToggleVisibility,
  onToggleHighlight,
  className,
  ...rest
}: Readonly<KeyframeEditorProps>) {
  const flatModel = useMemo(() => (model ? flatten(model) : []), [model]);
  const editorRef = useRef<HTMLDivElement>(null);
  const tracksRef = useRef<HTMLDivElement>(null);
  const [scrubbing, setScrubbing] = useState(false);

  const [temporarySelection, setTemporarySelection] = useState<Object3D[]>(selection);

  // If a selection change occurs outside of this component - focus the new selection in the list
  useEffect(() => {
    if (selection.map((o) => o.id).join() !== temporarySelection.map((o) => o.id).join()) {
      const last = selection.at(-1);
      if (last != null && editorRef.current) {
        const el = editorRef.current.querySelector(`[data-id='${last.id}']`);
        el?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }
    setTemporarySelection(selection);
  }, [selection, temporarySelection]);

  const handleTimelineScrub = useCallback<MouseEventHandler<HTMLDivElement>>(
    (e) => {
      if ((scrubbing || e.type === 'click') && editorRef.current != null && tracksRef.current != null) {
        e.preventDefault();
        const pixelOffset =
          e.pageX + editorRef.current.scrollLeft - (editorRef.current.offsetLeft + tracksRef.current.offsetLeft);
        const relativeOffset = pixelOffset / tracksRef.current.clientWidth;
        const relativeTime = relativeOffset * durationSeconds;
        const clampedRelativeTime = Math.min(Math.max(0, Math.round(relativeTime * 10) / 10), durationSeconds);
        onChangeTime(clampedRelativeTime);
      }
    },
    [durationSeconds, editorRef, onChangeTime, scrubbing]
  );

  const handleTimelineKeyScrub = useCallback<KeyboardEventHandler<HTMLDivElement>>(
    (e) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const direction = e.key === 'ArrowLeft' ? -1 : 1;
        const speed = e.shiftKey ? 10 : 1;
        onChangeTime(Math.min(Math.max(0, (time * 10 + speed * direction) / 10), durationSeconds));
      }

      if (e.key === 'Home') {
        e.preventDefault();
        onChangeTime(0);
      }

      if (e.key === 'End') {
        e.preventDefault();
        onChangeTime(durationSeconds);
      }
    },
    [durationSeconds, onChangeTime, time]
  );

  const handleSelection = useCallback(
    (selection: Object3D[]) => {
      setTemporarySelection(selection);
      onSelect?.(selection);
    },
    [onSelect]
  );

  return (
    <div ref={editorRef} className={classNames(styles.keyframeEditor, className)} {...rest}>
      <KeyFrameEditorObjects
        flatModel={flatModel}
        animation={animation}
        selection={selection}
        onSelect={handleSelection}
        onToggleVisibility={onToggleVisibility}
        onToggleHighlight={onToggleHighlight}
      />
      <div
        tabIndex={0}
        role="slider"
        aria-valuenow={time}
        aria-valuemin={0}
        aria-valuemax={durationSeconds}
        ref={tracksRef}
        className={styles.tracks}
        onKeyDown={handleTimelineKeyScrub}
        onClick={handleTimelineScrub}
        onMouseMove={handleTimelineScrub}
        onBlur={() => setScrubbing(false)}
        onMouseDown={() => setScrubbing(true)}
        onMouseUp={() => setScrubbing(false)}
      >
        <KeyframeEditorGrid durationSeconds={durationSeconds} />
        <KeyframeEditorTimes durationSeconds={durationSeconds} />
        <KeyframeEditorCursor durationSeconds={durationSeconds} time={time} />
        <KeyframeEditorTimelines
          flatModel={flatModel}
          animation={animation}
          durationSeconds={durationSeconds}
          selection={selection}
        />
      </div>
    </div>
  );
}

type KeyframeEditorObjectsProps = {
  flatModel: FlatObject[];
  animation?: Animation;
  selection: Object3D[];
  onSelect?: (selection: Object3D[]) => void;
  onToggleVisibility?: (object: Object3D) => void;
  onToggleHighlight?: (object: Object3D) => void;
};
const KeyFrameEditorObjects = memo(
  ({
    flatModel,
    animation,
    selection,
    onSelect,
    onToggleVisibility,
    onToggleHighlight,
  }: Readonly<KeyframeEditorObjectsProps>) => {
    const { t: i18n } = useTranslation();

    const handleSelect = useCallback(
      (object: Object3D, multiple: boolean) => {
        if (onSelect) {
          if (multiple && selection.includes(object)) {
            onSelect(selection.filter((selected) => selected !== object));
          } else if (multiple) {
            onSelect(Array.from(new Set([...selection, object])));
          } else {
            onSelect([object]);
          }
        }
      },
      [onSelect, selection]
    );

    return (
      <div className={styles.objects}>
        {flatModel.map(({ object, depth }) => {
          const channel = animation?.channels[object.name];
          const isSelected = selection.find((selected) => selected.name === object.name) != null;

          return (
            <div key={object.id} data-id={object.id} className={styles.object} data-selected={isSelected || undefined}>
              <div style={{ paddingLeft: depth * 16 }}>
                <button
                  type="button"
                  className={styles.checkbox}
                  tabIndex={-1}
                  title={
                    isSelected
                      ? i18n('instructions:keyframeEditor.deselect')
                      : i18n('instructions:keyframeEditor.select')
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelect(object, true);
                  }}
                >
                  <FontAwesomeIcon icon={isSelected ? faSquareCheck : faSquare} fixedWidth />
                </button>
              </div>
              <button
                type="button"
                className={styles.name}
                title={object.name}
                onClick={(e) => handleSelect(object, e.shiftKey)}
              >
                <FontAwesomeIcon className={styles.icon} icon={object instanceof Mesh ? faCube : faCubes} fixedWidth />
                <span>{object.name}</span>
              </button>
              <button
                type="button"
                className={styles.action}
                tabIndex={-1}
                title={
                  object.visible ? i18n('instructions:keyframeEditor.hide') : i18n('instructions:keyframeEditor.show')
                }
                onClick={() => onToggleVisibility?.(object)}
              >
                <FontAwesomeIcon icon={channel?.hidden ? faEyeSlash : faEye} fixedWidth />
              </button>
              <button
                type="button"
                className={styles.action}
                tabIndex={-1}
                title={
                  object.visible
                    ? i18n('instructions:keyframeEditor.highlight')
                    : i18n('instructions:keyframeEditor.removeHighlight')
                }
                onClick={() => onToggleHighlight?.(object)}
              >
                <FontAwesomeIcon icon={channel?.highlighted ? faLightbulbOn : faLightbulb} fixedWidth />
              </button>
            </div>
          );
        })}
      </div>
    );
  }
);

type KeyframeEditorGridProps = {
  durationSeconds: number;
};
const KeyframeEditorGrid = memo(({ durationSeconds }: Readonly<KeyframeEditorGridProps>) => (
  <div className={styles.grid}>
    {/* Render time indicators (+1 to make the range inclusive and show the end of the grid) */}
    {Array.from(Array(Math.ceil(durationSeconds + 1)).keys()).map((i) => (
      <span className={styles.line} key={`time-${i}`} />
    ))}
  </div>
));

type KeyframeEditorTimesProps = {
  durationSeconds: number;
};
const KeyframeEditorTimes = memo(({ durationSeconds }: Readonly<KeyframeEditorTimesProps>) => (
  <div className={styles.times}>
    {/* Render time indicators (+1 to make the range inclusive and show the end time) */}
    {Array.from(Array(Math.ceil(durationSeconds + 1)).keys()).map((i) => (
      <span className={styles.time} key={`time-${i}`}>
        {`${i + 1}s`}
      </span>
    ))}
  </div>
));

type KeyframeEditorTimelinesProps = {
  flatModel: FlatObject[];
  animation?: Animation;
  durationSeconds: number;
  selection: Object3D[];
};
const KeyframeEditorTimelines = memo(
  ({ flatModel, animation, durationSeconds, selection }: Readonly<KeyframeEditorTimelinesProps>) => (
    <div className={styles.timelines}>
      {flatModel.map(({ object }) => {
        const keyframes = animation?.channels[object.name]?.keyframes ?? [];

        return (
          <div
            key={object.id}
            className={styles.timeline}
            data-selected={selection.find((selected) => selected.name === object.name) != null || undefined}
          >
            {keyframes.length > 1 && (
              <div
                className={styles.keyframePath}
                style={{
                  left: `${(keyframes.at(0)!.time / durationSeconds) * 100}%`,
                  right: `${100 - (keyframes.at(-1)!.time / durationSeconds) * 100}%`,
                }}
              />
            )}
            {keyframes.map((keyframe) => (
              <div
                key={`${object.id}-${keyframe.time}`}
                className={styles.keyframe}
                style={{ left: `${(keyframe.time / durationSeconds) * 100}%` }}
              />
            ))}
          </div>
        );
      })}
    </div>
  )
);

type KeyframeEditorCursorProps = {
  time: number;
  durationSeconds: number;
};
const KeyframeEditorCursor = memo(({ time, durationSeconds }: Readonly<KeyframeEditorCursorProps>) => (
  <div
    className={classNames(styles.cursor, time < durationSeconds / 2 ? styles.left : styles.right)}
    data-time={`${time.toFixed(1)}s`}
    style={{ left: `${(time / durationSeconds) * 100}%` }}
  />
));
